import React, { useState, useEffect, useCallback } from 'react';
import { withAppHOC } from '../../../hoc';
import PageHeading from '../../../components/PageHeader/PageHeader';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import NoData from '../../../components/NoData/NoData';
import {
  MessageBox,
  Form,
  Fieldset,
  Button,
  ResetButton,
  Dropdown,
  TextInput,
  Label,
} from '../../../components/FormElements';
import { TableBuilder } from '../../../components/TableElements';
import { StyledFiltersContainer } from '../../../components/Styled';
import API from '../../../api';
import { API_RESPONSE_TYPES } from '../../../constants';
import { formatCurrency } from '../../../utils';
import moment from 'moment';

const AdminProfitReportTable = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [data, setData] = useState([]);
  const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));

  const resetForm = () => {
    setStartDate(moment().format('YYYY-MM-DD'));
    setEndDate(moment().format('YYYY-MM-DD'));
    readData(moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD'));
  };

  const readData = useCallback((startDate = '', endDate = '') => {
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    API.get(
      `/reports/admin-profit-report?startDate=${startDate}&endDate=${endDate}`
    )
      .then((response) => {
        const { status, message, data } = response.data;
        if (status === API_RESPONSE_TYPES.FAILURE) {
          setResponseStatus(status);
          setResponseMessage(message);
        }
        setData(data);
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILURE);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const filterData = (e) => {
    e.preventDefault();
    readData(startDate, endDate);
  };

  useEffect(() => {
    readData(startDate, endDate);
  }, []);

  return (
    <>
      <PageHeading {...props} canReloadProp={false} />
      <StyledFiltersContainer className="show">
        <Form method="POST" action="#" onSubmit={filterData}>
          <Fieldset>
            <Label>Start Date</Label>
            <TextInput
              type="date"
              value={startDate}
              onChange={setStartDate}
              placeholder="Start Date"
              disabled={isLoading === true}
            />
          </Fieldset>
          <Fieldset>
            <Label>End Date</Label>
            <TextInput
              type="date"
              value={endDate}
              onChange={setEndDate}
              placeholder="End Date"
              disabled={isLoading === true}
            />
          </Fieldset>
          <Fieldset>
            <Button disabled={isLoading === true} type="submit">
              Search
            </Button>
            <ResetButton
              disabled={isLoading === true}
              type="button"
              onClick={resetForm}
            >
              Clear
            </ResetButton>
          </Fieldset>
        </Form>
      </StyledFiltersContainer>
      <LoadingSpinner
        isLoading={responseStatus === '' && data?.length === 0 && isLoading}
      />
      <NoData
        status={
          responseStatus !== API_RESPONSE_TYPES.FAILURE &&
          !isLoading &&
          data?.length === 0
        }
        message={`No data found`}
      />
      <MessageBox status={responseStatus} message={responseMessage} />
      {data?.length > 0 && (
        <TableBuilder
          isLoading={data?.length !== 0 && isLoading}
          tableHeadings={[
            {
              title: 'Service',
              dataSelector: 'serviceName',
              dataType: 'string',
            },
            {
              title: 'Sale Amount',
              dataSelector: 'saleAmount',
              dataType: 'number',
              CellRenderer: (value) => formatCurrency(value),
            },
            {
              title: 'Commission Given',
              dataSelector: 'commissionGiven',
              dataType: 'number',
              CellRenderer: (value) => formatCurrency(value),
            },
            {
              title: 'Commission Earned',
              dataSelector: 'commissionEarned',
              dataType: 'number',
              CellRenderer: (value) => formatCurrency(value),
            },
            {
              title: 'Roffer',
              dataSelector: 'roffer',
              dataType: 'number',
              CellRenderer: (value) => formatCurrency(value),
            },
            {
              title: 'Profit Without Roffer',
              dataSelector: 'profitWithOutRoffer',
              dataType: 'number',
              CellRenderer: (value) => formatCurrency(value),
            },
            {
              title: 'Profit With Roffer',
              dataSelector: 'profitWithRoffer',
              dataType: 'number',
              CellRenderer: (value) => formatCurrency(value),
            },
          ]}
          tableData={data}
          tableFooter={[
            {
              value: '',
            },
            {
              value: formatCurrency(
                data.reduce(
                  (previousValue, currentValue) =>
                    previousValue + Number(currentValue.saleAmount),
                  0
                )
              ),
            },
            {
              value: formatCurrency(
                data.reduce(
                  (previousValue, currentValue) =>
                    previousValue + Number(currentValue.commissionGiven),
                  0
                )
              ),
            },
            {
              value: formatCurrency(
                data.reduce(
                  (previousValue, currentValue) =>
                    previousValue + Number(currentValue.commissionEarned),
                  0
                )
              ),
            },
            {
              value: formatCurrency(
                data.reduce(
                  (previousValue, currentValue) =>
                    previousValue + Number(currentValue.roffer),
                  0
                )
              ),
            },
            {
              value: formatCurrency(
                data.reduce(
                  (previousValue, currentValue) =>
                    previousValue + Number(currentValue.profitWithOutRoffer),
                  0
                )
              ),
            },
            {
              value: formatCurrency(
                data.reduce(
                  (previousValue, currentValue) =>
                    previousValue + Number(currentValue.profitWithRoffer),
                  0
                )
              ),
            },
          ]}
        />
      )}
    </>
  );
};

export default withAppHOC(AdminProfitReportTable);
